import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdArrowBackIosNew } from "react-icons/md";

function SchoolKOT() {
  const location = useLocation();
  const { items = [], hubName = "", date = "" } = location?.state || {};
  const navigate = useNavigate();
  const hasPrinted = useRef(false);

  const handlePrint = () => { if (items.length) window.print(); };

  useEffect(() => {
    if (hasPrinted.current || !items.length) return;
    const timer = setTimeout(() => { window.print(); hasPrinted.current = true; }, 500);
    return () => clearTimeout(timer);
  }, []);

  const activeItems = items.filter(o => {
    const s = (o.status || o.orderstatus || "").toLowerCase();
    return s === "confirmed" || s === "delivered";
  });
  const sessions = [...new Set(activeItems.map(o => o.session || o.slot || "—"))];

  return (
    <>
      <style>{`
        @media print {
          @page { margin: 4mm; size: 80mm auto; }
          .no-print { display: none !important; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          .kot-wrapper { padding: 0 !important; background: white !important; }
        }
      `}</style>

      {/* Top bar — hidden on print */}
      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", background: "#f5f5f5", borderBottom: "1px solid #ddd" }}>
        <MdArrowBackIosNew onClick={() => navigate(-1)} style={{ fontSize: "24px", cursor: "pointer", color: "#333" }} />
        <div style={{ fontWeight: "700", fontSize: "14px", fontFamily: "Inter" }}>
          🎓 {hubName}{date ? ` · ${date}` : ""} ({activeItems.length})
        </div>
        <button
          onClick={handlePrint}
          disabled={items.length === 0}
          style={{ background: "#6B8E23", color: "#fff", border: "none", padding: "8px 18px", borderRadius: "8px", fontWeight: "700", fontSize: "13px", cursor: "pointer", fontFamily: "Inter" }}
        >
          🖨 Print All
        </button>
      </div>

      {activeItems.length === 0 ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#888", fontFamily: "Inter" }}>No orders to print.</div>
      ) : (
        <div className="kot-wrapper" style={{ display: "flex", justifyContent: "center", padding: "16px", background: "#fff" }}>
          <div style={{
            width: "80mm",
            background: "#fff",
            padding: "8mm 6mm",
            fontFamily: "'Courier New', monospace",
            fontSize: "11px",
            lineHeight: "1.4",
          }}>

            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "8px" }}>
              <div style={{ fontSize: "15px", fontWeight: "900", letterSpacing: "1px" }}>DailyDish</div>
              <div style={{ fontSize: "11px", fontWeight: "700" }}>{hubName}</div>
              <div style={{ fontSize: "10px", color: "#555" }}>
                {date || new Date().toLocaleDateString("en-IN")} &nbsp;|&nbsp; {activeItems.length} orders
              </div>
              <div style={{ borderBottom: "1.5px dashed #000", margin: "6px 0" }} />
            </div>

            {/* Column header */}
            <div style={{ display: "flex", alignItems: "center", gap: "4px", padding: "5px 0 5px", borderBottom: "2px solid #000", marginBottom: "4px" }}>
              <span style={{ fontWeight: "900", fontSize: "13px", whiteSpace: "nowrap", minWidth: "20px" }}>#</span>
              <span style={{ fontWeight: "900", fontSize: "13px", whiteSpace: "nowrap", minWidth: "32px" }}>ID</span>
              <span style={{ fontWeight: "900", fontSize: "13px", flex: 1 }}>Name</span>
              <span style={{ fontWeight: "900", fontSize: "13px", whiteSpace: "nowrap", minWidth: "26px", textAlign: "center" }}>Cls</span>
              <span style={{ fontWeight: "900", fontSize: "13px", whiteSpace: "nowrap", minWidth: "20px", textAlign: "center" }}>Sec</span>
              <div style={{ width: "13px", flexShrink: 0 }} />
            </div>

            {/* One block per session */}
            {sessions.map((session, sIdx) => {
              const sessionOrders = activeItems.filter(o => (o.session || o.slot || "—") === session);
              return (
                <div key={session}>
                  {/* Order rows */}
                  {sessionOrders.map((order, idx) => {
                    const isTeacher = !order.studentClass && !order.studentSection;
                    return (
                      <div
                        key={order._id || idx}
                        style={{ display: "flex", alignItems: "flex-start", gap: "4px", padding: "3px 0", borderBottom: "1px dotted #ccc" }}
                      >
                        <span style={{ fontWeight: "900", fontSize: "13px", whiteSpace: "nowrap", minWidth: "20px" }}>
                          {idx + 1}
                        </span>
                        <span style={{ fontWeight: "900", fontSize: "13px", letterSpacing: "0.5px", whiteSpace: "nowrap", minWidth: "32px" }}>
                          {order.counterId || "—"}
                        </span>
                        <span style={{ fontWeight: "900", fontSize: "13px", flex: 1, wordBreak: "break-word" }}>
                          {order.studentName || order.username || "—"}{isTeacher ? " (T)" : ""}
                          {isTeacher && order.subjects ? (
                            <span style={{ display: "block", fontWeight: "700", fontSize: "10px", color: "#444" }}>
                              {order.subjects}
                            </span>
                          ) : null}
                        </span>
                        <span style={{ fontWeight: "900", fontSize: "13px", whiteSpace: "nowrap", minWidth: "26px", textAlign: "center" }}>
                          {order.studentClass || "—"}
                        </span>
                        <span style={{ fontWeight: "900", fontSize: "13px", whiteSpace: "nowrap", minWidth: "20px", textAlign: "center" }}>
                          {order.studentSection || "—"}
                        </span>
                        <div style={{ width: "13px", height: "13px", border: "1.5px solid #000", borderRadius: "2px", flexShrink: 0 }} />
                      </div>
                    );
                  })}

                  <div style={{ marginBottom: "6px" }} />
                </div>
              );
            })}

            {/* Footer */}
            <div style={{ borderTop: "1px dashed #000", marginTop: "6px", paddingTop: "4px", textAlign: "center", fontSize: "9px", color: "#888" }}>
              {new Date().toLocaleString("en-IN")}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SchoolKOT;